/*
* Broadcast Theme
*
* Use this file to add custom Javascript to Broadcast.  Keeping your custom
* Javascript in this fill will make it easier to update Broadcast. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/

(function() {
  // Add custom code below this line

  // set as a global variable from metafields
  const GIFT_CAMPAIGNS = window.GiftCampaigns || [];
  let giftLogicTimeoutId = null;
  const blockedGiftVariantIds = new Set();

  async function getCart() {
    const response = await fetch('/cart.js');

    if (!response.ok) {
      throw new Error(`Unable to load cart: ${response.status}`);
    }

    return response.json();
  }

  function getCartItemsElement() {
    return document.querySelector('cart-items');
  }

  function refreshCartUI() {
    const cartItemsEl = getCartItemsElement();
    if (cartItemsEl) {
      cartItemsEl.getCart();
    }
  }

  async function addGift(variantId) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.warn(`Gift add failed for variant ${variantId}:`, errorText);
      blockedGiftVariantIds.add(variantId);
      return false;
    }

    refreshCartUI();
    return true;
  }

  async function removeGift(lineKey) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });

    if (!response.ok) {
      return false;
    }

    refreshCartUI();
    return true;
  }

  async function syncGiftCampaigns() {
    const cart = await getCart();
    // Exclude all configured gift variants from qualification checks so
    // gifts never contribute to meeting any campaign threshold.
    const giftVariantIds = GIFT_CAMPAIGNS.map(campaign => campaign.gift_variant_id);
    const cartItemsExcludingGifts = cart.items.filter(item => !giftVariantIds.includes(item.id));
    // Vendor-specific campaigns take precedence over cart-wide campaigns.
    // If at least one vendor campaign qualifies, generic campaigns are blocked.
    const eligibleVendorCampaigns = GIFT_CAMPAIGNS.filter(campaign => {
      const normalizedVendor = (campaign.vendor || '').trim().toLowerCase();

      if (!normalizedVendor) {
        return false;
      }

      const thresholdCents = Math.round(campaign.threshold * 100);
      const matchedSubtotal = cartItemsExcludingGifts
        .filter(item => (item.vendor || '').toLowerCase() === normalizedVendor)
        .reduce((sum, item) => sum + item.final_line_price, 0);

      return matchedSubtotal >= thresholdCents;
    });
    const hasEligibleVendorCampaign = eligibleVendorCampaigns.length > 0;

    for (const campaign of GIFT_CAMPAIGNS) {
      const thresholdCents = Math.round(campaign.threshold * 100);
      const normalizedVendor = (campaign.vendor || '').trim().toLowerCase();
      // A campaign with `vendor` only looks at that vendor's items.
      // A campaign without `vendor` uses the whole non-gift cart.
      const matchedItems = normalizedVendor
        ? cartItemsExcludingGifts.filter(item => (item.vendor || '').toLowerCase() === normalizedVendor)
        : cartItemsExcludingGifts;
      const matchedSubtotal = matchedItems.reduce((sum, item) => sum + item.final_line_price, 0);

      const giftItem = cart.items.find(item => item.id === campaign.gift_variant_id);
      // Generic campaigns are only allowed when no vendor-specific campaign
      // is currently eligible, which prevents stacking multiple free gifts.
      const eligible = normalizedVendor
        ? matchedSubtotal >= thresholdCents
        : !hasEligibleVendorCampaign && matchedSubtotal >= thresholdCents;

      if (eligible && !giftItem) {
        if (blockedGiftVariantIds.has(campaign.gift_variant_id)) {
          return false;
        }

        return addGift(campaign.gift_variant_id);
      }

      if (!eligible && giftItem) {
        return removeGift(giftItem.key);
      }
    }

    return false;
  }

  document.addEventListener('theme:cart:change', () => {
    clearTimeout(giftLogicTimeoutId);
    // Broadcast can emit several cart-change events during one visual update.
    // Debounce them so we evaluate gift logic once after the cart settles.
    giftLogicTimeoutId = window.setTimeout(async () => {
      try {
        await syncGiftCampaigns();
      } catch (error) {
        console.warn('Gift campaign sync failed:', error);
      }
    }, 150);
  });

  // ^^ Keep your scripts inside this IIFE function call to 
  // avoid leaking your variables into the global scope.
})();
